
import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Mail, ArrowRight, RefreshCw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";

export default function EmailConfirmation() {
  const [email, setEmail] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    // Get the email from local storage
    const storedEmail = localStorage.getItem("pendingConfirmationEmail");
    if (storedEmail) {
      setEmail(storedEmail);
    } else {
      // If no email is stored, user shouldn't be on this page
      navigate("/login");
    }

    // Check authentication status
    const checkAuth = async () => {
      const { data } = await supabase.auth.getSession();
      if (data.session) {
        // User is authenticated, redirect to home
        localStorage.removeItem("pendingConfirmationEmail");
        navigate("/");
      }
    };

    // Check auth initially
    checkAuth();

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' || session) {
        localStorage.removeItem("pendingConfirmationEmail");
        navigate("/");
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate]);

  const handleResendEmail = async () => {
    if (!email || resending) return;
    
    setResending(true);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email
      });
      
      if (error) {
        throw error;
      }
      
      toast({
        title: "Confirmation email resent",
        description: "Please check your inbox and spam folder.",
      });
    } catch (error) {
      console.error("Error resending confirmation email:", error);
      toast({
        variant: "destructive",
        title: "Failed to resend email",
        description: "Please try again later.",
      });
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <Link to="/" className="inline-flex items-center justify-center">
            <div className="h-12 w-40 relative flex justify-center">
              <h1 className="text-2xl font-bold">OGMAI</h1>
            </div>
          </Link>
        </div>

        <Card>
          <CardHeader className="space-y-1">
            <div className="mx-auto bg-primary/10 p-4 rounded-full w-16 h-16 flex items-center justify-center">
              <Mail className="h-8 w-8 text-primary" />
            </div>
            <CardTitle className="text-2xl text-center">Check your email</CardTitle>
            <CardDescription className="text-center">
              We've sent a confirmation email to<br />
              <span className="font-medium">{email}</span>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg text-sm">
              <p className="mb-2 font-medium">Please verify your email address to continue:</p>
              <ol className="list-decimal pl-5 space-y-2">
                <li>Check your inbox for an email from OGMAI</li>
                <li>Click the verification link in the email</li>
                <li>You'll be automatically redirected after verification</li>
              </ol>
            </div>
            
            <Button 
              variant="outline" 
              className="w-full" 
              onClick={handleResendEmail}
              disabled={resending}
            >
              {resending ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Resending...
                </>
              ) : (
                "Resend confirmation email"
              )}
            </Button>
          </CardContent>
          <CardFooter className="flex flex-col space-y-4">
            <div className="text-sm text-center text-gray-500">
              Already confirmed your email?
            </div>
            <Button variant="ghost" asChild>
              <Link to="/login" className="w-full flex items-center justify-center">
                Go to login
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
